import React, { useEffect, useState, useRef } from 'react';
import { Button, Modal, Space, Spin, Table, Typography, Tooltip } from 'antd';
import { PlayCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Storage } from 'aws-amplify';
// import moment from 'moment'; // Removed - not needed in current implementation
import { useCreativeContext } from '../../../core/components/creatives/main/useCreativeContext';
import S3Image from '../CampaignLauncher/components/S3Image';

const { Text } = Typography;

export const CampaignRequestorVideoCreativesDialog = ({
  visible,
  setVisible,
  orgId,
  selectedCreatives,
  onAddSelections,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { fetchAdGroupCreatives, adGroupCreatives, loadingAdGroupCreatives } = useCreativeContext();
  const [processedAdGroups, setProcessedAdGroups] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');

  // Cache for S3 signed URLs - prevents re-fetching during same session
  // Map structure: { mediaKey: { url: string, timestamp: number } }
  const s3UrlCacheRef = useRef(new Map());
  const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes (S3 URLs valid for 10 min)

  // Cache for detected video durations - prevents re-detecting during same session
  // Map structure: { mediaKey: duration (in seconds) }
  const durationCacheRef = useRef(new Map());

  // Helper function to calculate total duration using detected durations
  const calculateDuration = (medias) => {
    if (!medias || medias.length === 0) return '--';

    const totalSeconds = medias.reduce((sum, media) => {
      return sum + (media.duration || 30); // Use detected duration or 30s fallback
    }, 0);

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Helper to get cached S3 URL if available and not expired
  const getCachedS3Url = (mediaKey) => {
    if (!mediaKey) return null;

    const cached = s3UrlCacheRef.current.get(mediaKey);
    if (!cached) return null;

    // Check if cache has expired
    const isExpired = Date.now() - cached.timestamp > CACHE_EXPIRY_MS;
    if (isExpired) {
      s3UrlCacheRef.current.delete(mediaKey);
      return null;
    }

    return cached.url;
  };

  // Helper to cache S3 URL
  const cacheS3Url = (mediaKey, url) => {
    if (!mediaKey) return;
    s3UrlCacheRef.current.set(mediaKey, { url, timestamp: Date.now() });
  };

  // Helper to get cached duration
  const getCachedDuration = (mediaKey) => {
    if (!mediaKey) return null;
    return durationCacheRef.current.get(mediaKey) || null;
  };

  // Helper to cache duration
  const cacheDuration = (mediaKey, duration) => {
    if (!mediaKey) return;
    durationCacheRef.current.set(mediaKey, duration);
  };

  // Helper to detect video duration on-demand (lazy loading)
  const detectVideoDuration = async (previewUrl, mediaKey) => {
    if (!previewUrl) return null;

    // Check cache first
    const cached = getCachedDuration(mediaKey);
    if (cached !== null) {
      return cached;
    }

    try {
      const duration = await new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          resolve(Math.round(video.duration));
        };
        video.onerror = () => resolve(null);
        video.src = previewUrl;
      });

      if (duration) {
        cacheDuration(mediaKey, duration);
      }
      return duration;
    } catch (error) {
      console.warn('Failed to detect duration:', error);
      return null;
    }
  };

  useEffect(() => {
    if (visible && orgId) {
      fetchAdGroupCreatives({
        variables: {
          where: {
            AND: [
              {
                org_some: {
                  id: orgId,
                },
              },
              {
                org_every: {
                  id: orgId,
                },
              },
              {
                medias_some: {
                  AND: [{ type: 'VIDEO' }, { fileType: 'MP4' }],
                },
              },
            ],
          },
        },
      });
    }
  }, [visible, orgId, fetchAdGroupCreatives]);

  // Clear caches when orgId changes (caches are org-specific)
  useEffect(() => {
    s3UrlCacheRef.current.clear();
    durationCacheRef.current.clear();
  }, [orgId]);

  // Process adGroupCreatives to get signed URLs for video previews
  useEffect(() => {
    const processAdGroups = async () => {
      if (!Array.isArray(adGroupCreatives)) {
        setProcessedAdGroups([]);
        return;
      }

      // Show processing state while generating S3 URLs
      // (Durations are detected lazily on-demand when user clicks to preview)
      setIsProcessing(true);

      try {
        const processed = await Promise.all(
          adGroupCreatives.map(async (adGroup) => {
            // Filter to only MP4 videos
            const videoMedias =
              adGroup.medias?.filter((media) => media.type === 'VIDEO' && media.fileType === 'MP4') ||
              [];

            // Process signed URLs for each video media
            const processedMedias = await Promise.all(
              videoMedias.map(async (media) => {
                try {
                  // Try to get URL from cache first to avoid redundant S3 calls
                  let previewUrl;
                  if (media.key) {
                    const cachedUrl = getCachedS3Url(media.key);
                    if (cachedUrl) {
                      previewUrl = cachedUrl;
                    } else {
                      // Not in cache, fetch from S3
                      previewUrl = await Storage.get(media.key, { expires: 600000 });
                      // Store in cache for future use
                      cacheS3Url(media.key, previewUrl);
                    }
                  } else {
                    previewUrl = media.url;
                  }

                  // Duration will be detected on-demand when user previews video (lazy loading)
                  // This saves 50-100 seconds on initial modal load
                  return { ...media, previewUrl, duration: null };
                } catch (e) {
                  console.warn('Failed to get signed URL for:', media.name, e);
                  return { ...media, previewUrl: media.url, duration: null };
                }
              })
            );

            return { ...adGroup, medias: processedMedias };
          })
        );

        setProcessedAdGroups(processed);
      } finally {
        // Always clear processing state when done (success or error)
        setIsProcessing(false);
      }
    };

    processAdGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adGroupCreatives]);

  // Video Modal Component
  const VideoModal = ({ videoUrl, visible, onClose }) => (
    <Modal open={visible} title="Video Player" onCancel={onClose} footer={null} width={800}>
      <video controls width="100%" style={{ maxHeight: '60vh' }}>
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </Modal>
  );

  // Video Thumbnail Component for Gallery
  const VideoThumbnail = ({ media, onClick }) => (
    <div
      onClick={onClick}
      style={{
        width: 80,
        height: 60,
        cursor: 'pointer',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        position: 'relative',
      }}
    >
      {media.previewUrl ? (
        <>
          <video
            src={media.previewUrl}
            type="video/mp4"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            muted
            preload="metadata"
          />
          {/* Play icon overlay */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '16px',
              color: 'white',
              textShadow: '0 0 4px rgba(0,0,0,0.5)',
            }}
          >
            ▶
          </div>
        </>
      ) : (
        <Text type="secondary" style={{ fontSize: 10 }}>
          No Preview
        </Text>
      )}
    </div>
  );

  // Table columns definition - Video-specific format
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 250,
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.medias?.length || 0} video{record.medias?.length !== 1 ? 's' : ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'medias',
      key: 'duration',
      width: 100,
      render: (medias) => (
        <Text strong style={{ fontFamily: 'monospace' }}>
          {calculateDuration(medias)}
        </Text>
      ),
    },
    {
      title: 'Gallery',
      dataIndex: 'medias',
      key: 'gallery',
      width: 300,
      render: (medias) => {
        if (!medias || medias.length === 0) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No videos
            </Text>
          );
        }

        const handleVideoClick = async (media) => {
          setSelectedVideoUrl(media.previewUrl);
          setVideoModalVisible(true);

          // Lazy load duration on video click if not already detected
          if (!media.duration && media.previewUrl && media.key) {
            const duration = await detectVideoDuration(media.previewUrl, media.key);
            if (duration) {
              // Update the media object with detected duration
              media.duration = duration;
              // Trigger re-render by updating processedAdGroups
              setProcessedAdGroups([...processedAdGroups]);
            }
          }
        };

        return (
          <Space size="small">
            {medias.slice(0, 4).map((media, index) => (
              <VideoThumbnail
                key={index}
                media={media}
                onClick={() => handleVideoClick(media)}
              />
            ))}
            {medias.length > 4 && (
              <Tooltip title={`${medias.length - 4} more videos`}>
                <div
                  style={{
                    width: 80,
                    height: 60,
                    border: '1px dashed #d9d9d9',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    +{medias.length - 4}
                  </Text>
                </div>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  // Set selected row keys based on selectedCreatives prop
  useEffect(() => {
    if (selectedCreatives && selectedCreatives.length > 0 && processedAdGroups.length > 0) {
      const keys = [];
      selectedCreatives.forEach((creative) => {
        // Find the creative in the processed list by name
        const adGroup = processedAdGroups.find((ag) => ag.name === creative.name);
        if (adGroup) {
          keys.push(adGroup.id);
        }
      });
      setSelectedRowKeys(keys);
    }
  }, [processedAdGroups, selectedCreatives]);

  // Row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  const handleAddSelections = () => {
    if (selectedRowKeys.length === 0) {
      onAddSelections([]);
      setVisible(false);
      return;
    }
    const selected = (processedAdGroups || [])
      .filter((item) => selectedRowKeys.includes(item.id))
      .map((adGroup) => ({
        ...adGroup,
        // Sort videos by duration (longest first) - videos without duration go to end
        medias: adGroup.medias?.sort((a, b) => {
          const durationA = a.duration || 0;
          const durationB = b.duration || 0;
          return durationB - durationA; // Descending order (longest first)
        }) || []
      }));
    onAddSelections(selected);
    setVisible(false);
  };

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 20,
          }}
        >
          <span>VIDEO AD GROUPS</span>
          <div>
            <Button type="default" onClick={handleAddSelections}>
              Add Selection(s) ({selectedRowKeys.length})
            </Button>
          </div>
        </div>
      }
      open={visible}
      onCancel={() => {
        setVisible(false);
      }}
      footer={null}
      width={1200}
    >
      {loadingAdGroupCreatives || isProcessing ? (
        <Space direction="vertical" style={{ width: '100%' }} align="center">
          <Spin />
          <Text type="secondary">{loadingAdGroupCreatives ? 'Loading video ad groups…' : 'Processing video files…'}</Text>
        </Space>
      ) : (
        <>
          <Table
            rowKey="id"
            rowSelection={rowSelection}
            columns={columns}
            dataSource={processedAdGroups || []}
            pagination={processedAdGroups?.length > 10 ? { pageSize: 10 } : false}
            scroll={{ y: 400 }}
            size="small"
          />

          {/* Video Player Modal */}
          <VideoModal
            videoUrl={selectedVideoUrl}
            visible={videoModalVisible}
            onClose={() => {
              setVideoModalVisible(false);
              setSelectedVideoUrl('');
            }}
          />
        </>
      )}
    </Modal>
  );
};

export default CampaignRequestorVideoCreativesDialog;
